var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as moment from 'moment';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useEffect, memo } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePromptOnLeave } from 'root/src/client/v2/form/hooks/usePromptOnLeave';
import { DARE_DESCRIPTION_MAX_LENGTH, DARE_HINT_MAX_LENGTH, GAME_DESCRIPTION_MAX_LENGTH, } from 'root/src/shared/descriptions/endpoints/schemas/requestSchemas/dropAddEventPayloadSchema';
import { CompletionType, RewardType, } from 'root/src/shared/rewards/constants';
import { useHashParam } from 'root/src/client/v2/common/hooks/useHashParam';
import { dropEventBudgetTypes, dropDareDifficultyLevelEnum, RewardForValue, } from 'root/src/shared/constants/dropEvent';
import { DarePlatform } from 'root/src/shared/@types/platforms';
import { youtubeUrlPattern } from 'root/src/shared/descriptions/formSchemas/addDropEventSchema';
import { useQueryParam } from 'use-query-params';
import { getDropEventQueryOptions } from 'root/src/client/v2/drop/hooks/useDropEvent';
import { StringQueryParam } from 'root/src/client/v2/queryParams/helpers/params.helper';
import { useQueryClient } from '@tanstack/react-query';
import { BATTLE_PASS_MAX_TIER, BATTLE_PASS_MIN_TIER } from 'root/src/shared/battlePass/fixtures/battlePass.schema';
import { useAutoSave } from 'root/src/client/v2/_pages/add-campaign/hooks/useAutoSave';
import { useAccountData } from 'root/src/client/v2/userData/hooks/useAccountData';
import { keyRewardSchema } from 'root/src/shared/rewards/fixtures/rewardsSchema.fixtures';
export const dareFormSchema = z.object({
    id: z
        .string()
        .optional(),
    title: z
        .string()
        .min(1, { message: 'Please enter a dare title' }),
    description: z
        .string()
        .min(1, {
        message: 'Please enter a dare description',
    })
        .max(DARE_DESCRIPTION_MAX_LENGTH, {
        message: `Description must be less than ${DARE_DESCRIPTION_MAX_LENGTH} characters`,
    }),
    estimatedTime: z.string().optional(),
    hint: z
        .string()
        .max(DARE_HINT_MAX_LENGTH, {
        message: `Hint must be less than ${DARE_HINT_MAX_LENGTH} characters`,
    })
        .optional(),
    platforms: z.array(z.nativeEnum(DarePlatform)).min(1, {
        message: 'Please select at least one platform',
    }),
    authPlatform: z.string().optional(),
    battlePass: z
        .preprocess((value) => {
        if (typeof value !== 'object' || !value || !('id' in value)) {
            return undefined;
        }
        if (!value.id) {
            return undefined;
        }
        return value;
    }, z.union([
        z.object({
            id: z.string({
                message: 'Please select a battle pass',
            }),
            tier: z
                .number({
                message: 'Please enter a valid tier',
            })
                .int({
                message: 'Please enter a valid tier',
            })
                .min(BATTLE_PASS_MIN_TIER, {
                message: 'Tier must be greater than 0',
            })
                .max(BATTLE_PASS_MAX_TIER, {
                message: 'Tier must be less than 1000',
            }),
        }).optional(),
        z.undefined(),
    ])),
    level: z.enum(Object.values(dropDareDifficultyLevelEnum)),
    rewardFor: z.union([
        z.literal(RewardForValue.CREATOR),
        z.literal(RewardForValue.FANS),
    ]),
    reservedProducts: z.coerce.number({
        message: 'Please enter a valid number of reserved products',
    }).int().nonnegative({
        message: 'Please enter a valid number of reserved products',
    }),
    games: z.array(z.object({
        label: z.string(),
        id: z.coerce.number(),
        value: z.coerce.number().optional(),
        image: z.string(),
    })).optional(),
    amount: z.coerce.number().int().positive({
        message: 'Please enter a valid amount of products',
    }),
    product: z.object({
        id: z.string(),
        name: z.string(),
        type: z.union([
            z.literal(RewardType.KEY),
            z.literal(RewardType.CODE),
            z.literal(RewardType.CUSTOM),
            z.literal(RewardType.BUNDLE),
            z.literal(RewardType.EXTERNAL_API_FULFILLED),
            z.literal(RewardType.PLACEHOLDER),
        ]),
    }),
    // TODO this probably deserves its own place in the codebase since
    //  we'll probably be using similar logic in multiple places
    expirationDate: z.preprocess((date) => {
        if (!date) {
            return undefined;
        }
        return moment.utc(date).format();
    }, z.string()
        .refine((value) => {
        if (!value) {
            return true;
        }
        const date = new Date(value);
        const isValidDate = !Number.isNaN(date);
        return isValidDate;
    }, { message: 'Please select a valid date' })
        .optional()
        .nullish()),
    disabled: z.boolean().optional(),
    completion: z.discriminatedUnion('type', [
        z.object({
            type: z.literal(CompletionType.REVIEW),
        }),
        z.object({
            type: z.literal(CompletionType.GAME_INTEGRATED),
            appId: z.string().min(1, {
                message: 'Please select an app ID',
            }),
            patternId: z.string().min(1, {
                message: 'Please select a completion pattern',
            }),
        }),
    ], {
        message: 'Please select a completion type',
    }),
    clipsOnly: z.boolean().optional(),
});
const initialProductItemSchema = z.discriminatedUnion('type', [
    z.object({
        id: z.string({
            required_error: 'Please select product',
        }).min(1, {
            message: 'Please select product',
        }),
        name: z.string(),
        cta: z.string(),
        url: z.string(),
        created: z.string(),
        type: z.literal(RewardType.CUSTOM, {
            errorMap: () => {
                return {
                    message: 'Please select product',
                };
            },
        }),
    }, {
        errorMap: () => {
            return {
                message: 'Please select product',
            };
        },
    }),
    z.object({
        id: z.string({
            required_error: 'Please select product',
        }).min(1, {
            message: 'Please select product',
        }),
        game: z.object({
            name: z.string(),
            boxArtTemplateUrl: z.string(),
            id: z.string(),
        }),
        created: z.string(),
        region: z.enum(keyRewardSchema.regionEnum),
        keyType: z.enum(keyRewardSchema.keyTypeEnum),
        keyTypeReadable: z.string(),
        platformReadable: z.string(),
        platform: z.enum(keyRewardSchema.platformEnum),
        type: z.literal(RewardType.KEY, {
            errorMap: () => {
                return {
                    message: 'Please select product',
                };
            },
        }),
        name: z.string(),
    }, {
        errorMap: () => {
            return {
                message: 'Please select product',
            };
        },
    }),
    z.object({
        id: z.string({
            required_error: 'Please select product',
        }).min(1, {
            message: 'Please select product',
        }),
        name: z.string(),
        created: z.string(),
        type: z.literal(RewardType.CODE, {
            errorMap: () => {
                return {
                    message: 'Please select product',
                };
            },
        }),
    }, { errorMap: () => {
            return {
                message: 'Please select product',
            };
        },
    }),
    z.object({
        amount: z.number(),
        name: z.string(),
        reservedForCreators: z.number(),
        type: z.literal(RewardType.BUNDLE, {
            errorMap: () => {
                return {
                    message: 'Please select product',
                };
            },
        }),
        rewards: z.array(z.object({
            amount: z.number(),
            id: z.string(),
            name: z.string(),
            type: z.union([
                z.literal(RewardType.KEY),
                z.literal(RewardType.CODE),
                z.literal(RewardType.CUSTOM),
            ]),
        })),
        id: z
            .string({
            required_error: 'Please select product',
        }).min(1, {
            message: 'Please select product',
        }),
    }, {
        errorMap: () => {
            return {
                message: 'Please select product',
            };
        },
    }),
], {
    errorMap: () => {
        return {
            message: 'Please select product',
        };
    },
});
const assetSchema = z.union([
    z.object({
        url: z.string(),
        fileName: z.string().optional(),
        fileHash: z.string().optional(),
    }),
    z.custom(),
    z.object({
        desktop: z.string().optional(),
        mobile: z.string().optional(),
        fileHash: z.string().optional(),
    }),
]);
const breakpointsSchema = z.array(z.object({
    maxDares: z.coerce.number().int().nonnegative(),
    viewershipCount: z.coerce.number().int().nonnegative(),
}));
const baseCampaignFormSchema = z.object({
    id: z.string()
        .optional(),
    brandId: z.string().optional(),
    assets: z.array(assetSchema),
    isUserActivated: z.boolean().optional(),
    budget: z.object({
        type: z.enum([dropEventBudgetTypes.CAMPAIGN, dropEventBudgetTypes.MONTHLY]),
        amount: z.coerce.number().int().nonnegative(),
    }).optional(),
    isPrivate: z.boolean(),
    similarGames: z.array(z.object({
        label: z.string(),
        id: z.coerce.number(),
        value: z.coerce.number(),
        image: z.string(),
    })),
    youtubeURL: z.string().refine((value) => {
        if (!value) {
            return true;
        }
        const isValidUrl = new RegExp(youtubeUrlPattern).test(value);
        return isValidUrl;
    }).optional(),
    // TODO this probably deserves its own place in the codebase since
    //  we'll probably be using similar logic in multiple places
    liveDate: z.string()
        .refine((value) => {
        if (!value) {
            return true;
        }
        const date = new Date(value);
        const isValidDate = !Number.isNaN(date);
        return isValidDate;
    }, { message: 'Please select a valid live date' })
        .optional()
        .nullish(),
    gameDescriptionBody: z
        .string()
        .max(GAME_DESCRIPTION_MAX_LENGTH, {
        message: `Description must be less than ${GAME_DESCRIPTION_MAX_LENGTH} characters`,
    })
        .optional(),
    cta: z.string()
        .optional(),
    brandLogoUrl: z.string().min(1, { message: 'Please select a brand logo' }).optional(),
    url: z.object({
        custom: z.boolean(),
        value: z.string(),
    }),
    campaignName: z.string().min(1, { message: 'Please enter a campaign url' }),
    dares: z.array(dareFormSchema),
    isGameFree: z.boolean()
        .default(false),
    initialProduct: z
        .array(z.object({
        amount: z.coerce.number().int().positive({
            message: 'Please enter a valid amount of products',
        }),
        reservedForCreators: z.coerce.number().int().nonnegative({
            message: 'Please enter a valid number of reserved products',
        }),
    }).and(initialProductItemSchema)).optional(),
    messageToCreator: z.string()
        .optional(),
    viewershipBreakpoints: z.object({
        twitch: breakpointsSchema,
        youtube: breakpointsSchema,
        youtubeShorts: breakpointsSchema,
        tiktok: breakpointsSchema,
    }),
});
const baseGameCampaignSchema = z.object({
    isMultiGame: z.literal(false),
    gameObj: z.object({
        label: z.string(),
        id: z.coerce.number(),
        value: z.coerce.number(),
        image: z.string(),
        slug: z.string().optional(),
    }, {
        errorMap: () => {
            return {
                message: 'Please select a game',
            };
        },
    }).optional(),
    dropTitle: z.string().optional(),
});
const baseMultiGameCampaignSchema = z.object({
    isMultiGame: z.literal(true),
    campaignProduct: z.string().min(1),
    campaignThumbnail: z.object({
        url: z.string(),
        fileName: z.string().optional(),
        fileHash: z.string().optional(),
    }).optional(),
});
const publishMultiGameCampaignSchema = z.object({
    isMultiGame: z.literal(true),
    campaignProduct: z.string()
        .min(1, { message: 'Please select a product name' }),
    campaignThumbnail: z.object({
        url: z.string(),
        fileName: z.string().optional(),
        fileHash: z.string().optional(),
    }),
});
const publishGameCampaignSchema = z.object({
    isMultiGame: z.literal(false),
    gameObj: z.object({
        label: z.string(),
        id: z.coerce.number(),
        value: z.coerce.number(),
        image: z.string(),
    }, {
        message: 'Please select a game',
    }),
    brandLogoUrl: z.string({ message: 'Please select a brand logo' }).min(1),
    dropTitle: z.string()
        .min(1, { message: 'Please enter a drop title' }),
});
export const publishCampaignFormSchema = z.discriminatedUnion('isMultiGame', [
    publishGameCampaignSchema,
    publishMultiGameCampaignSchema,
]).and(baseCampaignFormSchema.and(z.object({
    youtubeURL: z.string()
        .regex(new RegExp(youtubeUrlPattern), {
        message: 'Please enter a valid YouTube URL',
    })
        .optional(),
    liveDate: z.string()
        .refine((value) => {
        const date = new Date(value);
        const isValidDate = !Number.isNaN(date);
        return isValidDate;
    }, { message: 'Please select a valid live date' }),
})));
export const addCampaignFormSchema = z.discriminatedUnion('isMultiGame', [
    baseGameCampaignSchema,
    baseMultiGameCampaignSchema,
]).and(baseCampaignFormSchema);
export function useAddCampaignFormContext() {
    return useFormContext();
}
export const AddCampaignFormContextProvider = memo(({ children }) => {
    const accountData = useAccountData();
    const [isPublish, setIsPublish] = useHashParam('publish');
    const { getDefaultValue } = useDropFormDefaultValue();
    const form = useForm({
        defaultValues: getDefaultValue,
        resolver: zodResolver(isPublish ? publishCampaignFormSchema : addCampaignFormSchema),
        mode: 'all',
        reValidateMode: 'onChange',
    });
    useEffect(() => {
        if (Object.values(form.formState.errors).length) {
            console.error('[/add-campaign] form error', {
                errors: form.formState.errors,
                values: form.getValues(),
                form,
            });
        }
    }, [form.formState]);
    useEffect(() => {
        if (isPublish) {
            void form.trigger(undefined, {
                shouldFocus: false,
            });
            setIsPublish(undefined);
        }
    }, [isPublish]);
    useEffect(() => {
        var _a;
        const isBrandLogoFilled = form.getValues('brandLogoUrl');
        const brandLogoUrl = (_a = accountData === null || accountData === void 0 ? void 0 : accountData.brandLogo) === null || _a === void 0 ? void 0 : _a.big;
        if (!isBrandLogoFilled && brandLogoUrl) {
            form.setValue('brandLogoUrl', brandLogoUrl);
        }
    }, [accountData]);
    return (<FormProvider {...form}>
			<PromptOnLeaveWrapper>
				{children}
			</PromptOnLeaveWrapper>
		</FormProvider>);
});
const PromptOnLeaveWrapper = ({ children }) => {
    const form = useAddCampaignFormContext();
    const { autoSave } = useAutoSave();
    usePromptOnLeave(() => {
        if (form.formState.isDirty) {
            void autoSave();
            return 'You have unsaved changes. Are you sure you want to leave?';
        }
        return undefined;
    });
    return (<>{children}</>);
};
const useDropFormDefaultValue = () => {
    var _a, _b;
    const accountData = useAccountData();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, setDropId] = useQueryParam('dropId', StringQueryParam());
    const queryClient = useQueryClient();
    const defaultValue = {
        assets: [],
        isMultiGame: false,
        isPrivate: false,
        url: {
            custom: false,
            value: '',
        },
        cta: '',
        dropTitle: '',
        youtubeURL: '',
        gameDescriptionBody: '',
        similarGames: [],
        liveDate: '',
        brandLogoUrl: (_b = (_a = accountData === null || accountData === void 0 ? void 0 : accountData.brandLogo) === null || _a === void 0 ? void 0 : _a.big) !== null && _b !== void 0 ? _b : '',
        dares: [],
        initialProduct: [],
        messageToCreator: '',
        isGameFree: false,
        campaignName: '',
        viewershipBreakpoints: {
            twitch: [{ viewershipCount: 5, maxDares: 0 }],
            tiktok: [{ viewershipCount: 10000, maxDares: 0 }],
            youtube: [{ viewershipCount: 100, maxDares: 0 }],
            youtubeShorts: [{ viewershipCount: 100, maxDares: 0 }],
        },
    };
    const getDefaultValue = () => __awaiter(void 0, void 0, void 0, function* () {
        var _c, _d, _e, _f;
        const dropId = new URL(window.location.href).searchParams.get('dropId');
        if (dropId) {
            try {
                const drop = yield queryClient.fetchQuery(Object.assign(Object.assign({}, getDropEventQueryOptions({
                    id: dropId,
                })), { retry: 1, retryDelay: 500 }));
                setDropId(undefined);
                if (drop) {
                    const brandId = (_c = drop.brand) === null || _c === void 0 ? void 0 : _c.id;
                    return Object.assign(Object.assign(Object.assign({}, defaultValue), drop), { brandId, brandLogoUrl: (_d = drop.brandLogoUrl) !== null && _d !== void 0 ? _d : (_f = (_e = drop.brand) === null || _e === void 0 ? void 0 : _e.brandLogo) === null || _f === void 0 ? void 0 : _f.big });
                }
            }
            catch (err) {
                console.error(err);
            }
            setDropId(undefined);
        }
        return defaultValue;
    });
    return {
        getDefaultValue,
    };
};
